<template>
  <div class="whats-new-widget">
    <div v-if="hidden" class="hidden-state d-flex align-center justify-space-between px-1 py-2">
      <span class="text-caption grey--text">What's New is hidden</span>
      <v-btn x-small text color="green darken-1" @click="show">Show</v-btn>
    </div>

    <!-- Survey mode: the survey takes over the whole card until submitted or dismissed -->
    <div v-else-if="surveyNoteId" class="survey-mode">
      <div class="d-flex align-center mb-2">
        <v-btn x-small text color="grey" :disabled="voting" @click="closeSurvey">
          <v-icon x-small left>mdi-arrow-left</v-icon>Back to updates
        </v-btn>
      </div>

      <div v-if="voteSaved" class="text-center py-6" aria-live="polite">
        <v-icon color="green darken-1" size="40">mdi-check-circle-outline</v-icon>
        <p class="text-body-2 font-weight-medium mt-2 mb-1">Vote recorded</p>
        <p class="text-caption grey--text mb-4">
          Your county voted &ldquo;{{ voteSaved.option }}&rdquo;
        </p>
        <v-btn small text color="green darken-1" @click="closeSurvey">Done</v-btn>
      </div>

      <template v-else-if="surveyNote && hasActiveSurvey(surveyNote)">
        <div class="text-caption grey--text mb-1">{{ surveyNote.title }}</div>
        <div id="whatsNewSurveyQuestion" class="text-body-2 font-weight-medium mb-2">
          {{ surveyNote.survey.question }}
        </div>

        <div class="unison-note d-flex align-start px-3 py-2 mb-2">
          <v-icon small color="blue darken-1" class="mr-2 mt-1">mdi-account-group-outline</v-icon>
          <span class="text-caption">
            One vote per county &mdash; make sure your office is voting in unison.
          </span>
        </div>

        <div v-if="surveyNote.countyVote" class="text-caption grey--text mb-1">
          Your county's current vote:
          <span class="font-weight-bold">{{ surveyNote.countyVote.option }}</span>
          <template v-if="surveyNote.countyVote.username">
            (cast by {{ surveyNote.countyVote.username }})
          </template>
          &mdash; submitting again changes it.
        </div>

        <v-radio-group
          v-model="selectedOption"
          class="mt-1 mb-0"
          hide-details
          aria-labelledby="whatsNewSurveyQuestion"
        >
          <v-radio
            v-for="option in surveyNote.survey.options"
            :key="option"
            :label="option"
            :value="option"
            color="green darken-1"
            class="mb-1"
          />
        </v-radio-group>

        <div v-if="voteError" role="alert" class="text-caption red--text text--darken-1 mt-2">
          {{ voteError }}
        </div>

        <div class="d-flex align-center justify-end mt-3">
          <v-btn small text color="grey" class="mr-2" :disabled="voting" @click="closeSurvey">
            Cancel
          </v-btn>
          <!-- dark only while enabled: a DISABLED dark v-btn takes the dark theme's
               disabled palette (white-on-white) and the button disappears entirely. -->
          <v-btn
            small
            color="green darken-1"
            :dark="!!selectedOption"
            :loading="voting"
            :disabled="!selectedOption"
            @click="submitVote"
          >
            {{ surveyNote.countyVote ? 'Change vote' : 'Submit vote' }}
          </v-btn>
        </div>
      </template>

      <!-- The note vanished from a refetch, or its survey was closed server-side -->
      <div v-else class="text-center py-6">
        <v-icon color="grey lighten-1" size="32">mdi-poll</v-icon>
        <p class="text-caption grey--text mt-2 mb-4">This survey is no longer open.</p>
        <v-btn small text color="green darken-1" @click="closeSurvey">Back to updates</v-btn>
      </div>
    </div>

    <template v-else>
      <div class="d-flex align-center justify-end mb-1">
        <v-btn x-small text color="grey" @click="hide">
          <v-icon x-small left>mdi-eye-off-outline</v-icon>Hide
        </v-btn>
      </div>
      <div v-if="loading" class="text-center py-6">
        <v-progress-circular indeterminate size="24" color="green darken-1" />
      </div>
      <div v-else-if="fetchError" class="text-center py-4">
        <v-icon color="grey lighten-1" size="32">mdi-alert-circle-outline</v-icon>
        <p class="text-caption grey--text mt-2">Unable to load updates</p>
      </div>
      <div v-else-if="updates.length === 0" class="text-center py-4">
        <v-icon color="grey lighten-1" size="32">mdi-newspaper-variant-outline</v-icon>
        <p class="text-caption grey--text mt-2">No recent updates</p>
      </div>
      <div v-else class="updates-list">
        <div
          v-for="update in updates"
          :key="update._id"
          class="update-item d-flex align-start"
        >
          <v-chip
            small
            :color="statusColor(update.status)"
            dark
            class="mr-3 mt-1 flex-shrink-0 status-chip"
            pill
          >
            {{ statusLabel(update.status) }}
          </v-chip>
          <div>
            <div class="text-body-2 font-weight-medium">
              {{ update.title }}
              <span v-if="update.version" class="text-caption grey--text ml-1">v{{ update.version }}</span>
            </div>
            <div class="text-caption grey--text">{{ update.body }}</div>
            <a
              v-if="update.link"
              :href="update.link"
              target="_blank"
              rel="noopener noreferrer"
              class="text-caption font-weight-medium guide-link d-inline-flex align-center mt-1 mr-3"
            >
              View guide
              <v-icon
                x-small
                :color="$vuetify.theme.dark ? 'green lighten-2' : 'green darken-1'"
                class="ml-1"
              >mdi-open-in-new</v-icon>
            </a>
            <template v-if="hasActiveSurvey(update)">
              <v-btn
                x-small
                text
                color="green darken-1"
                class="survey-btn mt-1 px-1"
                @click="openSurvey(update)"
              >
                <v-icon x-small left>mdi-poll</v-icon>
                {{ update.countyVote ? 'Change your vote' : 'Take survey' }}
              </v-btn>
              <div v-if="update.countyVote" class="text-caption grey--text">
                Your county voted &ldquo;{{ update.countyVote.option }}&rdquo;
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { clients } from '../../../util/clients';

const { backendRest } = clients.direct;
const STORAGE_KEY = 'whatsNewWidget_hidden';

export default {
  name: 'WhatsNewWidget',
  data: () => ({
    updates: [],
    loading: true,
    fetchError: false,
    hidden: localStorage.getItem(STORAGE_KEY) === 'true',
    surveyNoteId: null,
    selectedOption: null,
    voting: false,
    voteError: '',
    voteSaved: null,
  }),
  computed: {
    ...mapGetters('auth/token', ['authUser']),
    /* Resolved from `updates` by id on every render, never held as a reference: a refetch
       replaces the array, and a held reference would keep offering a survey that the new data
       says is closed or gone. When the lookup misses, the template shows "no longer open". */
    surveyNote() {
      return this.updates.find((u) => u._id === this.surveyNoteId) || null;
    },
  },
  async created() {
    if (!this.hidden) await this.fetchUpdates();
  },
  methods: {
    async fetchUpdates() {
      if (this.authUser === 'anonymous') { this.loading = false; return; }
      this.loading = true;
      this.fetchError = false;
      try {
        const resp = await backendRest.get('/dashboard/release-notes');
        /* The endpoint filters to published and sorts newest-first before it answers, so this
           keeps the ten newest published notes. Do not re-sort here: the backend's tie-break for
           two notes published in the same second is _id descending, which this list cannot
           reproduce in a way that would stay in step with it. */
        this.updates = (resp.data || []).slice(0, 10);
      } catch (e) {
        console.error('WhatsNewWidget: failed to load', e);
        this.fetchError = true;
      } finally {
        this.loading = false;
      }
    },
    hasActiveSurvey(update) {
      return Boolean(update && update.survey && update.survey.active
        && update.survey.question && (update.survey.options || []).length > 0);
    },
    openSurvey(update) {
      this.surveyNoteId = update._id;
      // Pre-select the county's standing vote so "submit again" reads as changing it.
      this.selectedOption = update.countyVote ? update.countyVote.option : null;
      this.voteError = '';
      this.voteSaved = null;
    },
    closeSurvey() {
      this.surveyNoteId = null;
      this.selectedOption = null;
      this.voteError = '';
      this.voteSaved = null;
    },
    async submitVote() {
      // Captured before the await: closeSurvey() during the request nulls the computed.
      const note = this.surveyNote;
      if (!note || !this.selectedOption) return;
      this.voting = true;
      this.voteError = '';
      try {
        const resp = await backendRest.post('/dashboard/release-notes/survey-vote', {
          releaseNoteId: note._id,
          option: this.selectedOption,
        });
        const saved = resp.data || {};
        /* The list entry keeps showing the recorded vote after the takeover closes. Vue 2
           cannot see a brand-new key on an object it is already tracking, so the note is
           replaced in the array rather than mutated. */
        const countyVote = {
          option: saved.option,
          username: saved.username,
          updatedAt: saved.updatedAt,
        };
        this.updates = this.updates.map((u) => (
          u._id === note._id ? { ...u, countyVote } : u
        ));
        this.voteSaved = countyVote;
      } catch (e) {
        console.error('WhatsNewWidget: vote failed', e);
        const msg = e && e.response && e.response.data && e.response.data.error;
        this.voteError = (typeof msg === 'string' && msg)
          ? msg
          : 'Unable to record the vote. Please try again.';
        /* A 4xx means the server's view of the survey disagrees with ours (closed, option list
           changed). Refetch so the takeover reconciles — the computed surveyNote picks up the
           new state and flips to "no longer open" when the survey really is gone. */
        if (e && e.response && e.response.status >= 400 && e.response.status < 500) {
          this.fetchUpdates();
        }
      } finally {
        this.voting = false;
      }
    },
    hide() {
      // A hidden widget must not re-open into a stale survey panel later.
      this.closeSurvey();
      this.hidden = true;
      localStorage.setItem(STORAGE_KEY, 'true');
    },
    async show() {
      this.hidden = false;
      localStorage.setItem(STORAGE_KEY, 'false');
      await this.fetchUpdates();
    },
    statusColor(status) {
      const colors = {
        deployed: 'green darken-1',
        'in-uat': 'blue darken-1',
        'coming-soon': 'orange darken-1',
        survey: 'purple darken-1',
      };
      return colors[status] || 'grey';
    },
    statusLabel(status) {
      const labels = {
        deployed: 'Live',
        'in-uat': 'Testing',
        'coming-soon': 'Coming Soon',
        survey: 'Survey',
      };
      return labels[status] || (status || 'Update');
    },
    async refresh() {
      if (!this.hidden) await this.fetchUpdates();
    },
  },
};
</script>

<style scoped>
.update-item {
  padding: 10px 12px;
  border-radius: 10px;
  transition: background 0.15s ease;
  margin-bottom: 6px;
}

.update-item:hover {
  background: var(--widget-hover-bg, rgba(0, 0, 0, 0.03));
}

.update-item:last-child {
  margin-bottom: 0;
}

.updates-list {
  max-height: 320px;
  overflow-y: auto;
}

.updates-list::-webkit-scrollbar {
  width: 4px;
}

.updates-list::-webkit-scrollbar-thumb {
  background: var(--widget-scrollbar-thumb, #c8e6c9);
  border-radius: 4px;
}

.guide-link {
  text-decoration: none;
  color: var(--widget-ok-text, #2e7d32);
}

.guide-link:hover {
  text-decoration: underline;
}

.status-chip {
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px;
  min-width: 72px;
  justify-content: center;
}

.hidden-state {
  border: 1px dashed var(--widget-dashed-border, #e0e0e0);
  border-radius: 8px;
}

.survey-btn {
  text-transform: none;
  letter-spacing: 0;
}

.unison-note {
  border-radius: 8px;
  background: var(--widget-tint-info, rgba(25, 118, 210, 0.08));
}
</style>
